import React from 'react';
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  ListSubheader
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles({
  level1: {
    paddingLeft: 8,
    pointerEvents: 'none',
    backgroundColor: '#A5A5A5',
    '&:hover': {
      backgroundColor: '#A5A5A5'
    }
  },
  level2: {
    paddingLeft: 24,
    pointerEvents: 'none',
    backgroundColor: '#CECECE',
    '&:hover': {
      backgroundColor: '#CECECE'
    }
  },
  level3: {
    paddingLeft: 40,
    pointerEvents: 'none',
    backgroundColor: '#E5E5E5',
    '&:hover': {
      backgroundColor: '#E5E5E5'
    }
  },
  level4: {
    paddingLeft: 56,
    backgroundColor: '#FFFFFF'
  },
  groupLabel: {
    cursor: 'default',
    pointerEvents: 'none',
    '&.MuiListSubheader-root': {
      backgroundColor: 'rgba(103, 106, 108, 0.3)',
      color: 'rgba(0, 0, 0, 0.87)',
      lineHeight: '20px',
      padding: '8px 16px'
    }
  },
  groupValues: {
    '&.MuiButtonBase-root': {
      paddingLeft: '32px'
    }
  }
});

const SelectInput = props => {
  const {
    label,
    required,
    variant,
    margin,
    fullWidth,
    size,
    placeholder,
    name,
    disabled,
    onFocus,
    onBlur,
    key,
    error,
    helperText,
    value,
    onChange,
    options,
    customStyles,
    inputProps,
    selectProps,
    groupBy
  } = props;

  const classes = useStyles();

  const { list = [], keyId, valueId, displayValueId, isDisabled } = options;
  const safeList = Array.isArray(list) ? list : [];

  const onFocusCallback = event => {
    if (typeof onFocus === 'function') {
      onFocus(event);
    }
  };

  const onBlurCallback = event => {
    if (typeof onBlur === 'function') {
      onBlur(event);
    }
  };

  const handleChange = event => {
    if (typeof onChange === 'function') {
      onChange(event);
    }
  };

  const onClickItem = (event, option) => {
    if (typeof option.onClickText === 'function') {
      option.onClickText(event);
    }
  };

  const getValue = (option, optionValue) => {
    if (typeof optionValue === 'object') {
      let value;
      optionValue.every(val => {
        let data = val === 'option' ? option : option[val];
        if (data) {
          value = data;
        } else {
          return true;
        }
      });
      return value;
    } else {
      return optionValue === 'option' ? option : option[optionValue];
    }
  };

  const groupedList = list => {
    let uniqueList = [...new Set(list.map(element => element[groupBy]))];
    return uniqueList;
  }

  return (
    <TextField
      {...(customStyles && { style: customStyles })}
      {...(inputProps && { InputProps: inputProps })}
      {...(selectProps && { SelectProps: selectProps })}
      label={label}
      required={required}
      variant={variant || 'outlined'}
      margin={margin || 'none'}
      fullWidth={fullWidth}
      size={size || 'medium'}
      placeholder={placeholder}
      name={name}
      disabled={disabled}
      onFocus={onFocusCallback}
      onBlur={onBlurCallback}
      key={key}
      error={error}
      helperText={helperText}
      value={value}
      onChange={handleChange}
      select>
      {safeList.length === 0 ? (
        <MenuItem key="none" value="none" disabled>
          No options
        </MenuItem>
      ) : (
        groupBy ? (
        groupedList(safeList).map(groupName => (
          [
            <ListSubheader className={classes.groupLabel}>{groupName}</ListSubheader>,
            ...safeList?.filter(option => option[groupBy] === groupName)?.map(item => (
              <MenuItem
                key={getValue(item, keyId)}
                value={getValue(item, valueId)}
                className={classes.groupValues}
                disabled={isDisabled || item.isDisabled}
                {...(item.customStyles && { style: item.customStyles })}
                {...(item.className && { className: classes[item.className] })}>
                {getValue(item, displayValueId)}
              </MenuItem>
            ))
          ]
        ))) : (
        safeList?.map(option => (
          <MenuItem
            key={getValue(option, keyId)}
            value={getValue(option, valueId)}
            disabled={isDisabled || option.isDisabled}
            {...(option.customStyles && { style: option.customStyles })}
            {...(option.className && { className: classes[option.className] })}>
            {option.hasIcon ? (
              <>
                <ListItemIcon
                  {...(option.iconStyle && { style: option.iconStyle })}>
                  {option.iconElement}
                </ListItemIcon>
                <ListItemText onClick={event => onClickItem(event, option)}>
                  {option.displayText}
                </ListItemText>
              </>
            ) : (
              getValue(option, displayValueId)
            )}
          </MenuItem>
        )))
      )}
    </TextField>
  );
};

SelectInput.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  variant: PropTypes.string,
  margin: PropTypes.string,
  fullWidth: PropTypes.bool,
  size: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  key: PropTypes.string,
  error: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.object,
  customStyles: PropTypes.object,
  inputProps: PropTypes.object,
  selectProps: PropTypes.object
};

export default SelectInput;
